import { useCallback, useState } from 'react';

import { DEFAULT_PAGE_SIZE } from 'consts';
import { useApiAccess } from 'contexts/AccessProvider';
import { useListAccountsQuery } from 'gql';
import { usePagination, useSortBy } from 'hooks';
import { downloadCSV, formatAmount } from 'utils';

import AccountColumn from './components/AccountColumn';

interface AccountData {
  address: string | undefined;
  free: any;
  reserved: any;
  publicAddress: string | null | undefined;
  extrinsicCount: any;
}

function useAccountList() {
  const { sessionKey } = useApiAccess();

  const [accountData, setAccountData] = useState<Array<AccountData>>();
  const { PageController, setTotalDataCount, pageSize, pageNumber } = usePagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    shouldUpdateUrl: true,
  });

  const { sortField, sortOrder, onSort } = useSortBy();

  const { loading: isLoading } = useListAccountsQuery({
    variables: {
      sessionKey,
      page: { size: pageSize, number: pageNumber },
      ...(sortField && {
        sort: { field: sortField, order: sortOrder },
      }),
    },
    onCompleted(data) {
      setTotalDataCount(data.listAccounts.count);
      const accountList = data.listAccounts.data?.map((account) => {
        return {
          address: account?.address,
          free: account?.data?.data?.free,
          reserved: account?.data?.data?.reserved,
          publicAddress: account?.publicAddress,
          extrinsicCount: account?.extrinsicCount,
        };
      });
      setAccountData(accountList);
    },

    fetchPolicy: 'cache-and-network',
  });
  const accountColumn = AccountColumn();

  const handleDownload = useCallback(() => {
    if (!accountData || accountData.length === 0) {
      return;
    }

    const accountHeaders = ['Address', 'Extrinsics', 'Balance'];
    const accountRows = accountData.map((account) => [
      account?.publicAddress,
      account?.extrinsicCount,
      formatAmount(account.free, 4),
    ]);

    const fileName = `Accounts ${accountData[0].publicAddress} - ${
      accountData[accountData.length - 1].publicAddress
    }`;

    downloadCSV(fileName, accountHeaders, accountRows);
  }, [accountData]);

  return {
    PageController,
    accountData,
    isLoading,
    pageSize,
    sortOrder,
    onSort,
    accountColumn,
    handleDownload,
  };
}

export default useAccountList;
